import { useQuery } from "@apollo/client";
import { CircularProgress } from "@mui/material";
import { filterCatalogs } from "../../../graphql/queries/filterCatalogs";
import { listRewards } from "../../../graphql/queries/listRewards";

const CatalogsSelectView = ({ data }) => {
  // Safely extract data with fallbacks
  const catalogRefs = data?.catalog_refs || [];
  const catalogIds = catalogRefs.map((ref) => ref.catalog_id || ref.catalogId);
  const reward_ids = data?.reward_ids || [];

  // Fetch catalog details
  const {
    data: catalogsData,
    loading: catalogsLoading,
    error: catalogsError,
  } = useQuery(filterCatalogs, {
    variables: {
      filters: {
        status: ["APPROVED"],
        catalogId: catalogIds,
      },
    },
    skip: catalogIds.length === 0,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  // Fetch rewards filtered by catalog IDs
  const {
    data: rewardsData,
    loading: rewardsLoading,
    error: rewardsError,
  } = useQuery(listRewards, {
    variables: {
      filter: {
        isActive: true,
        catalogs: catalogIds,
      },
      page: 0,
      size: 1000,
    },
    skip: catalogIds.length === 0,
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  // Safely extract catalog names with error handling
  const getCatalogNames = () => {
    try {
      if (!catalogsData?.filterCatalogs?.items) return [];

      return catalogsData.filterCatalogs.items
        .map((item) => {
          if (!item?.catalogName) return null;

          return item.catalogName;
        })
        .filter(Boolean);
    } catch (error) {
      console.error("Error processing catalog names:", error);
      return [];
    }
  };

  // Safely extract reward names with error handling
  const getRewardNames = () => {
    try {
      const allRewardsForCatalogs = rewardsData?.listRewards?.items || [];
      if (!Array.isArray(allRewardsForCatalogs)) {
        return [];
      }

      const selectedRewards = allRewardsForCatalogs.filter((reward) => {
        try {
          return reward?.id && reward_ids.includes(reward.id);
        } catch (error) {
          console.error("Error filtering reward:", error);
          return false;
        }
      });

      return selectedRewards.map((reward) => reward?.name).filter(Boolean);
    } catch (error) {
      console.error("Error processing reward names:", error);
      return [];
    }
  };

  const catalogNames = getCatalogNames();
  const rewardNames = getRewardNames();

  const isLoading =
    (catalogIds.length > 0 && catalogsLoading) ||
    (catalogIds.length > 0 && rewardsLoading);

  const hasError = catalogsError || rewardsError;

  // Safe render functions
  const renderCatalogValue = () => {
    if (isLoading) {
      return <CircularProgress size={20} />;
    }

    if (hasError) {
      return (
        <span className="field-value error">Error loading catalog details</span>
      );
    }

    if (catalogNames.length > 0) {
      return <span className="field-value">{catalogNames.join(", ")}</span>;
    }

    return <span className="field-value">-</span>;
  };

  const renderRewardValue = () => {
    if (isLoading) {
      return <CircularProgress size={20} />;
    }

    if (hasError) {
      return <span className="field-value error">Error loading reward details</span>;
    }

    if (rewardNames.length > 0) {
      return <span className="field-value">{rewardNames.join(", ")}</span>;
    }

    return <span className="field-value">-</span>;
  };

  return (
    <div className="display-flex flex-direction-column gap-2">
      <h4>Catalogs and Rewards</h4>
      <div className="additional-fields-container">
        {/* Catalogs Section */}
        <div className="field-pair" style={{ flexBasis: "31% !important" }}>
          <span className="field-label">Selected Catalogs: </span>
        </div>
        <div className="field-pair-two-column">{renderCatalogValue()}</div>

        {/* Rewards Section */}
        <div className="field-pair" style={{ flexBasis: "31% !important" }}>
          <span className="field-label">Selected Rewards: </span>
        </div>
        <div className="field-pair-two-column">{renderRewardValue()}</div>
      </div>
    </div>
  );
};

export default CatalogsSelectView;
